import React from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { PhilRole, RoleProvider, useRole } from './RoleContext';
import PhilLayout from './PhilLayout';
import ProfileGate from './ProfileGate';
import PhilDashboard from './pages/PhilDashboard';
import ApplicationSection from './pages/ApplicationSection';
import ApplicationReview from './pages/ApplicationReview';
import AssignedApplicants from './pages/AssignedApplicants';
import ScoreApplicant from './pages/ScoreApplicant';
import InternalPipeline from './pages/InternalPipeline';
import InternalApplicationDetail from './pages/InternalApplicationDetail';
import InternalReports from './pages/InternalReports';
import AdminSettings from './pages/AdminSettings';
import AdminQuestions from './pages/AdminQuestions';
import AdminEvaluatorQuestions from './pages/AdminEvaluatorQuestions';
import AdminPipelineColumns from './pages/AdminPipelineColumns';
import AdminPriorities from './pages/AdminPriorities';
import AdminResources from './pages/AdminResources';
import AdminEmailTemplates from './pages/AdminEmailTemplates';
import AdminTeam from './pages/AdminTeam';
import Resources from './pages/Resources';

// Public (unauthenticated) auth screens — the custom UI that replaces the
// Cognito Hosted UI. Rendered outside AuthGate so signed-out users can reach them.
import Login from './auth/Login';
import SignUp from './auth/SignUp';
import VerifyEmail from './auth/VerifyEmail';
import ForgotPassword from './auth/ForgotPassword';
import ResetPassword from './auth/ResetPassword';
import NewPasswordChallenge from './auth/NewPasswordChallenge';
import EmailMfaChallenge from './auth/EmailMfaChallenge';

// Administrator (FIL) screens — the Priority Intelligence workflow, formerly the
// standalone FIL Portal, now rendered inside the Phil shell under the admin role.
import Dashboard from '../pages/Dashboard';
import FeedbackPriorities from '../pages/FeedbackPriorities';
import FIProfile from '../pages/FIProfile';
import SchedulePriorityCall from '../pages/SchedulePriorityCall';
import CalNotification from '../pages/CalNotification';
import CallReview from '../pages/CallReview';
import NotesExport from '../pages/NotesExport';
import ScoringMatrix from '../pages/ScoringMatrix';
import ThankYouEmail from '../pages/ThankYouEmail';

/** Single app entry. Public auth screens render OUTSIDE RoleProvider so their
 *  mount never triggers the `/me` fetch (which would 401 and bounce back to
 *  /login in a reload loop). Everything else is the authenticated app. */
export default function PhilApp() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<SignUp />} />
      <Route path="/verify" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/new-password" element={<NewPasswordChallenge />} />
      <Route path="/mfa" element={<EmailMfaChallenge />} />
      <Route path="/*" element={<AuthedApp />} />
    </Routes>
  );
}

/** The authenticated application: owns the role context and all persona routes. */
function AuthedApp() {
  return (
    <RoleProvider>
      <AuthGate>
        <ProfileGate>
          <PhilLayout>
            <AppRoutes />
          </PhilLayout>
        </ProfileGate>
      </AuthGate>
    </RoleProvider>
  );
}

/** Block the app until we know who the user is; unauthenticated → login redirect. */
function AuthGate({ children }: { children: React.ReactNode }) {
  const { status } = useRole();
  if (status === 'authenticated') return <>{children}</>;
  return (
    <div style={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center', color: '#64748b', fontFamily: 'system-ui, sans-serif' }}>
      {status === 'loading' ? 'Loading…' : 'Redirecting to sign in…'}
    </div>
  );
}

function AppRoutes() {
  const { role } = useRole();

  return (
    <Routes>
      {/* Landing depends on persona. */}
      <Route path="/" element={<Navigate to={roleHome(role)} replace />} />

      {/* --- Applicant (founders) --- */}
      <Route path="/apply" element={<PhilDashboard />} />
      <Route path="/apply/resources" element={<Resources />} />
      <Route path="/apply/review" element={<ApplicationReview />} />
      <Route path="/apply/:sectionNum" element={<ApplicationSection />} />

      {/* --- Evaluator (FI) --- */}
      <Route path="/evaluate" element={<AssignedApplicants />} />
      <Route path="/evaluate/scoring/:appId" element={<ScoreApplicant />} />
      <Route path="/evaluate/scoring" element={<Navigate to="/evaluate" replace />} />

      {/* --- Administrator (FIL): Priority Intelligence --- */}
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/feedback-priorities" element={<FeedbackPriorities />} />
      <Route path="/feedback-priorities/scoring" element={<ScoringMatrix />} />
      <Route path="/fi/:fiId" element={<FIProfile />} />
      <Route path="/fi/:fiId/schedule-call" element={<SchedulePriorityCall />} />
      <Route path="/fi/:fiId/notes" element={<NotesExport />} />
      <Route path="/cal-notifications/:fiId" element={<CalNotification />} />
      {/* LiveCall merged into CallReview; keep old links working. */}
      <Route path="/calls/active/:callId" element={<RedirectToReview />} />
      <Route path="/calls/:callId/review" element={<CallReview />} />
      <Route path="/calls/:callId/thank-you" element={<ThankYouEmail />} />

      {/* --- Administrator (FIL): F6S application admin --- */}
      <Route path="/admin/pipeline" element={<InternalPipeline />} />
      <Route path="/admin/applications/:appId/:tab" element={<InternalApplicationDetail />} />
      <Route path="/admin/applications/:appId" element={<InternalApplicationDetail />} />
      <Route path="/admin/reports" element={<InternalReports />} />

      {/* --- Administrator: content management --- */}
      <Route path="/admin/settings" element={<AdminSettings />} />
      <Route path="/admin/questions" element={<AdminQuestions />} />
      <Route path="/admin/evaluator-questions" element={<AdminEvaluatorQuestions />} />
      <Route path="/admin/pipeline-columns" element={<AdminPipelineColumns />} />
      <Route path="/admin/priorities" element={<AdminPriorities />} />
      <Route path="/admin/resources" element={<AdminResources />} />
      <Route path="/admin/email-templates" element={<AdminEmailTemplates />} />
      <Route path="/admin/team" element={<AdminTeam />} />
      <Route path="/admin/*" element={<Navigate to="/admin/pipeline" replace />} />

      <Route path="*" element={<Navigate to={roleHome(role)} replace />} />
    </Routes>
  );
}

/** Old transcript-entry route now lives on the merged Post-Call Review page. */
function RedirectToReview() {
  const { callId } = useParams<{ callId: string }>();
  return <Navigate to={`/calls/${callId}/review`} replace />;
}

function roleHome(role: PhilRole): string {
  if (role === 'applicant') return '/apply';
  if (role === 'evaluator') return '/evaluate';
  return '/dashboard'; // admin → Priority Intelligence
}
